import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const statutColors = {
  en_attente: "#fb8c00",
  confirmee: "#43a047",
  refusee: "#e53935",
  archivee: "#757575",
};

const statutLabels = {
  en_attente: "En attente",
  confirmee: "Confirmée",
  refusee: "Refusée",
  archivee: "Archivée",
};

const champsLabels = {
  edition_id: "Édition",
  groupe_id: "Groupe de chants",
  occasion_id: "Occasion",
  date: "Date",
  heure: "Heure",
  lieu: "Lieu",
  notes: "Notes",
  occasion_education: "Occasion éducative",
  occasion_autre: "Autre occasion",
  lieu_type: "Type de lieu",
  lieu_precision: "Détails lieu",
  nb_personnes: "Nombre de personnes",
  responsable_fete: "Responsable fête",
  responsable_adjoint1: "Responsable adjoint 1",
  responsable_adjoint2: "Responsable adjoint 2",
  presentateur1: "Présentateur 1",
  presentateur2: "Présentateur 2",
  langue_chants: "Langue des chants",
  groupe_local: "Groupe local",
  groupe_ville_origine: "Ville du groupe",
  materiel_audio: "Matériel audio",
  publicite_radio: "Publicité radio",
  recitateur1: "Récitateur 1",
  recitateur2: "Récitateur 2",
  cadeaux: "Cadeaux",
  equipe_reception: "Équipe réception",
  equipe_securite: "Équipe sécurité",
  equipe_service: "Équipe service",
  equipe_nettoyage: "Équipe nettoyage",
  equipe_vente: "Équipe vente",
  equipe_restauration: "Équipe restauration",
  responsable_table: "Responsable table",
};

const steps = ["Informations générales", "Responsables", "Équipes", "Chants & Divers"];

const defaultForm = {
  edition_id: "", groupe_id: "", occasion_id: "", date: "", heure: "", lieu: "", notes: "",
  occasion_education: "", occasion_autre: "", lieu_type: "", lieu_precision: "",
  nb_personnes: "", responsable_fete: "", responsable_adjoint1: "", responsable_adjoint2: "",
  presentateur1: "", presentateur2: "", langue_chants: "", groupe_local: false,
  groupe_ville_origine: "", materiel_audio: false, publicite_radio: false,
  recitateur1: "", recitateur2: "", cadeaux: "", equipe_reception: "",
  equipe_securite: "", equipe_service: "", equipe_nettoyage: "", equipe_vente: "",
  equipe_restauration: "", responsable_table: "",
};

function StepAdmin1({ form, setForm, groupes, occasions, editions }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Édition" select fullWidth value={form.edition_id}
          onChange={(e) => setForm((prev) => ({ ...prev, edition_id: e.target.value }))}>
          {editions.map((e) => <MenuItem key={e.id} value={e.id}>{e.libelle}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Groupe de chants" select fullWidth value={form.groupe_id}
          onChange={(e) => setForm((prev) => ({ ...prev, groupe_id: e.target.value }))}>
          {groupes.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.nom} — {Array.isArray(g.type_chants) ? g.type_chants.join(", ") : g.type_chants}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Occasion" select fullWidth value={form.occasion_id}
          onChange={(e) => setForm((prev) => ({ ...prev, occasion_id: e.target.value }))}>
          {occasions.map((o) => <MenuItem key={o.id} value={o.id}>{o.libelle}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }} {...f("date")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Heure" type="time" fullWidth InputLabelProps={{ shrink: true }} {...f("heure")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Nombre de personnes" type="number" fullWidth {...f("nb_personnes")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <FormLabel>Type de lieu</FormLabel>
          <RadioGroup row {...f("lieu_type")}>
            <FormControlLabel value="Salle extérieure" control={<Radio />} label="Salle extérieure" />
            <FormControlLabel value="Centre LACM" control={<Radio />} label="Centre LACM" />
            <FormControlLabel value="Autre" control={<Radio />} label="Autre" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Lieu précis" fullWidth {...f("lieu")} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Détails supplémentaires sur le lieu" fullWidth multiline rows={2} {...f("lieu_precision")} />
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Occasion liée à l&apos;Education</FormLabel>
          <RadioGroup row {...f("occasion_education")}>
            <FormControlLabel value="Fin de l'année scolaire" control={<Radio />} label="Fin de l'année scolaire" />
            <FormControlLabel value="Fêtes des diplômés" control={<Radio />} label="Fêtes des diplômés" />
            <FormControlLabel value="Autre" control={<Radio />} label="Autre" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {form.occasion_education === "Autre" && (
        <Grid item xs={12}>
          <TextField label="Préciser l'occasion" fullWidth {...f("occasion_autre")} />
        </Grid>
      )}
    </Grid>
  );
}

function StepAdmin2({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable de la fête" fullWidth multiline rows={2} {...f("responsable_fete")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable adjoint 1" fullWidth multiline rows={2} {...f("responsable_adjoint1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Responsable adjoint 2" fullWidth multiline rows={2} {...f("responsable_adjoint2")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Premier présentateur" fullWidth {...f("presentateur1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Deuxième présentateur" fullWidth {...f("presentateur2")} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Notes" fullWidth multiline rows={3} {...f("notes")} />
      </Grid>
    </Grid>
  );
}

function StepAdmin3({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe réception" fullWidth multiline rows={3} {...f("equipe_reception")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe sécurité" fullWidth multiline rows={3} {...f("equipe_securite")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe service" fullWidth multiline rows={3} {...f("equipe_service")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe nettoyage" fullWidth multiline rows={3} {...f("equipe_nettoyage")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe vente" fullWidth multiline rows={3} {...f("equipe_vente")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Équipe restauration" fullWidth multiline rows={3} {...f("equipe_restauration")} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Responsable table principale" fullWidth {...f("responsable_table")} />
      </Grid>
    </Grid>
  );
}

function StepAdmin4({ form, setForm }) {
  const f = (field) => ({
    value: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });
  const sw = (field) => ({
    checked: form[field],
    onChange: (e) => setForm((prev) => ({ ...prev, [field]: e.target.checked })),
  });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <FormLabel>Langue des chants souhaitée</FormLabel>
          <RadioGroup row {...f("langue_chants")}>
            <FormControlLabel value="Arabe" control={<Radio />} label="Arabe" />
            <FormControlLabel value="Français" control={<Radio />} label="Français" />
            <FormControlLabel value="Les deux" control={<Radio />} label="Les deux" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel control={<Switch {...sw("groupe_local")} />} label="Groupe local à la ville" />
        {!form.groupe_local && (
          <TextField label="Ville du groupe souhaité" fullWidth sx={{ mt: 2 }} {...f("groupe_ville_origine")} />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Premier récitateur de Qour'an" fullWidth {...f("recitateur1")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Deuxième récitateur de Qour'an" fullWidth {...f("recitateur2")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField label="Cadeaux à distribuer" fullWidth {...f("cadeaux")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <MDBox display="flex" flexDirection="column" gap={1}>
          <FormControlLabel control={<Switch {...sw("materiel_audio")} />} label="Besoin de matériel audio" />
          <FormControlLabel control={<Switch {...sw("publicite_radio")} />} label="Publicité dans une radio" />
        </MDBox>
      </Grid>
    </Grid>
  );
}

function Reservations() {
  const [reservations, setReservations] = useState([]);
  const [editions, setEditions] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [filtreEdition, setFiltreEdition] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("actives");
  const [openAction, setOpenAction] = useState(false);
  const [openFiche, setOpenFiche] = useState(false);
  const [openAffecter, setOpenAffecter] = useState(false);
  const [openHistorique, setOpenHistorique] = useState(false);
  const [openModifier, setOpenModifier] = useState(false);
  const [selected, setSelected] = useState(null);
  const [motifRefus, setMotifRefus] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [groupesDisponibilites, setGroupesDisponibilites] = useState([]);
  const [groupeSelectionne, setGroupeSelectionne] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [activeStep, setActiveStep] = useState(0);

  const fetchReservations = async () => {
    try {
      const [resRes, editionsRes, groupesRes, occasionsRes] = await Promise.all([
        api.get(`/reservations${filtreEdition ? `?edition_id=${filtreEdition}` : ""}`),
        api.get("/editions"),
        api.get("/groupes"),
        api.get("/occasions"),
      ]);
      setReservations(resRes.data);
      setEditions(editionsRes.data);
      setGroupes(groupesRes.data);
      setOccasions(occasionsRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [filtreEdition]);

  const handleOpenModifier = (reservation) => {
    setForm({
      edition_id: reservation.edition_id || "",
      groupe_id: reservation.groupe_id || "",
      occasion_id: reservation.occasion_id || "",
      date: reservation.date ? reservation.date.slice(0, 10) : "",
      heure: reservation.heure ? reservation.heure.slice(0, 5) : "",
      lieu: reservation.lieu || "",
      notes: reservation.notes || "",
      occasion_education: reservation.occasion_education || "",
      occasion_autre: reservation.occasion_autre || "",
      lieu_type: reservation.lieu_type || "",
      lieu_precision: reservation.lieu_precision || "",
      nb_personnes: reservation.nb_personnes || "",
      responsable_fete: reservation.responsable_fete || "",
      responsable_adjoint1: reservation.responsable_adjoint1 || "",
      responsable_adjoint2: reservation.responsable_adjoint2 || "",
      presentateur1: reservation.presentateur1 || "",
      presentateur2: reservation.presentateur2 || "",
      langue_chants: reservation.langue_chants || "",
      groupe_local: reservation.groupe_local || false,
      groupe_ville_origine: reservation.groupe_ville_origine || "",
      materiel_audio: reservation.materiel_audio || false,
      publicite_radio: reservation.publicite_radio || false,
      recitateur1: reservation.recitateur1 || "",
      recitateur2: reservation.recitateur2 || "",
      cadeaux: reservation.cadeaux || "",
      equipe_reception: reservation.equipe_reception || "",
      equipe_securite: reservation.equipe_securite || "",
      equipe_service: reservation.equipe_service || "",
      equipe_nettoyage: reservation.equipe_nettoyage || "",
      equipe_vente: reservation.equipe_vente || "",
      equipe_restauration: reservation.equipe_restauration || "",
      responsable_table: reservation.responsable_table || "",
    });
    setSelected(reservation);
    setActiveStep(0);
    setOpenModifier(true);
  };

  const handleSubmitModifier = async () => {
    setLoading(true);
    try {
      await api.put(`/reservations/${selected.id}/modifier-admin`, form);
      fetchReservations();
      setOpenModifier(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (reservation, actionType) => {
    setSelected(reservation);
    setAction(actionType);
    setMotifRefus("");
    setOpenAction(true);
  };

  const handleOpenFiche = (reservation) => {
    setSelected(reservation);
    setOpenFiche(true);
  };

  const handleOpenAffecter = async (reservation) => {
    setSelected(reservation);
    setGroupeSelectionne(null);
    try {
      const date = reservation.date.slice(0, 10);
      const res = await api.get(`/groupes/disponibilites/${date}`);
      setGroupesDisponibilites(res.data);
    } catch (err) {
      console.error(err);
    }
    setOpenAffecter(true);
  };

  const handleOpenHistorique = async (reservation) => {
    setSelected(reservation);
    try {
      const res = await api.get(`/reservations/${reservation.id}/historique`);
      setHistorique(res.data);
    } catch (err) {
      console.error(err);
    }
    setOpenHistorique(true);
  };

  const handleCloseAction = () => {
    setOpenAction(false);
    setSelected(null);
    setMotifRefus("");
    setAction("");
  };

  const handleCloseFiche = () => {
    setOpenFiche(false);
    setSelected(null);
  };

  const handleCloseAffecter = () => {
    setOpenAffecter(false);
    setSelected(null);
    setGroupeSelectionne(null);
    setGroupesDisponibilites([]);
  };

  const handleCloseHistorique = () => {
    setOpenHistorique(false);
    setSelected(null);
    setHistorique([]);
  };

  const handleValider = async () => {
    setLoading(true);
    try {
      await api.put(`/reservations/${selected.id}/valider`, {
        statut: action === "confirmer" ? "confirmee" : "refusee",
        motif_refus: action === "refuser" ? motifRefus : null,
      });
      fetchReservations();
      handleCloseAction();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoquer = async (id) => {
    if (window.confirm("Révoquer cette confirmation ?")) {
      try {
        await api.put(`/reservations/${id}/revoquer`);
        fetchReservations();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleReconsiderer = async (id) => {
  if (window.confirm("Remettre cette réservation en attente ?")) {
    try {
      await api.put(`/reservations/${id}/valider`, {
        statut: "en_attente",
        motif_refus: null,
      });
      fetchReservations();
    } catch (err) {
      console.error(err);
    }
  }
};

  const handleAffecter = async () => {
    if (!groupeSelectionne) return;
    setLoading(true);
    try {
      await api.put(`/reservations/${selected.id}/affecter`, {
        groupe_affecte_id: groupeSelectionne,
      });
      fetchReservations();
      handleCloseAffecter();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette réservation ?")) {
      try {
        await api.delete(`/reservations/${id}`);
        fetchReservations();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const Row = ({ label, value }) =>
    value ? (
      <MDTypography variant="body2" mb={0.5}>
        <strong>{label} :</strong> {value}
      </MDTypography>
    ) : null;

  const reservationsFiltrees = reservations.filter((r) => {
    if (filtreStatut === "actives") return r.statut !== "archivee";
    if (filtreStatut === "tous") return true;
    return r.statut === filtreStatut;
  });

  const stepContent = [
    <StepAdmin1 key="s1" form={form} setForm={setForm} groupes={groupes} occasions={occasions} editions={editions} />,
    <StepAdmin2 key="s2" form={form} setForm={setForm} />,
    <StepAdmin3 key="s3" form={form} setForm={setForm} />,
    <StepAdmin4 key="s4" form={form} setForm={setForm} />,
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Toutes les réservations
                </MDTypography>
                <MDTypography variant="caption" color="white">
                  {reservationsFiltrees.length} réservation(s)
                </MDTypography>
              </MDBox>

              {/* Filtres */}
              <MDBox px={3} pt={2} pb={1} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption" fontWeight="medium" color="text">
                    Édition :
                  </MDTypography>
                  <Chip label="Toutes" onClick={() => setFiltreEdition("")} size="small"
                    sx={{ cursor: "pointer", bgcolor: filtreEdition === "" ? "#1976d2" : "transparent",
                      color: filtreEdition === "" ? "white" : "text.primary", border: "1px solid #1976d2" }} />
                  {editions.map((e) => (
                    <Chip key={e.id} label={e.libelle} size="small" onClick={() => setFiltreEdition(e.id)}
                      sx={{ cursor: "pointer",
                        bgcolor: filtreEdition === e.id ? e.active ? "#43a047" : "#757575" : "transparent",
                        color: filtreEdition === e.id ? "white" : "text.primary",
                        border: `1px solid ${e.active ? "#43a047" : "#757575"}` }} />
                  ))}
                </MDBox>
                <MDBox sx={{ width: "1px", height: 24, bgcolor: "#e0e0e0" }} />
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption" fontWeight="medium" color="text">
                    Statut :
                  </MDTypography>
                  {["actives", "tous", "en_attente", "confirmee", "refusee", "archivee"].map((s) => (
                    <Chip key={s}
                      label={s === "actives" ? "Actives" : s === "tous" ? "Toutes" : statutLabels[s]}
                      size="small" onClick={() => setFiltreStatut(s)}
                      sx={{ cursor: "pointer",
                        bgcolor: filtreStatut === s ? s === "actives" || s === "tous" ? "#1976d2" : statutColors[s] : "transparent",
                        color: filtreStatut === s ? "white" : "text.primary",
                        border: `1px solid ${s === "actives" || s === "tous" ? "#1976d2" : statutColors[s]}`,
                        fontWeight: filtreStatut === s ? "bold" : "normal" }} />
                  ))}
                </MDBox>
              </MDBox>

              <MDBox pt={2} px={3} pb={3}>
                <Grid container spacing={3}>
                  {reservationsFiltrees.map((r) => (
                    <Grid item xs={12} md={6} lg={4} key={r.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee", borderRadius: 3,
                        opacity: r.statut === "archivee" ? 0.7 : 1 }}>
                        <MDBox display="flex" justifyContent="flex-end" mb={0.5}>
                          <Chip label={statutLabels[r.statut]} size="small"
                            sx={{ bgcolor: statutColors[r.statut], color: "white", fontWeight: "bold" }} />
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1565c0" }}>
                          {r.directeur_ville}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={0.5} mb={0.25}>
                          <Icon sx={{ fontSize: 15, color: "#546e7a" }}>music_note</Icon>
                          <MDTypography variant="body2" fontWeight="medium" color="text">{r.groupe_nom}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5} mb={1}>
                          <Icon sx={{ fontSize: 15, color: "#546e7a" }}>person</Icon>
                          <MDTypography variant="body2" color="text">{r.directeur_nom}</MDTypography>
                        </MDBox>
                        {r.edition_libelle && (
                          <Chip label={r.edition_libelle} size="small"
                            sx={{ bgcolor: "#e3f2fd", color: "#1565c0", mb: 1.5, fontSize: 11 }} />
                        )}
                        <MDBox sx={{ bgcolor: "#f8f9fa", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                          <MDBox display="flex" alignItems="center" gap={1} mb={0.75}>
                            <Icon sx={{ fontSize: 16, color: "#546e7a" }}>event</Icon>
                            <MDTypography variant="body2" fontWeight="medium" color="text">
                              {new Date(r.date).toLocaleDateString("fr-FR")}
                              {r.heure ? ` à ${r.heure.slice(0, 5)}` : ""}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={1} mb={0.75}>
                            <Icon sx={{ fontSize: 16, color: "#546e7a" }}>location_on</Icon>
                            <MDTypography variant="body2" fontWeight="medium" color="text">{r.lieu}</MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: 16, color: "#546e7a" }}>celebration</Icon>
                            <MDTypography variant="body2" fontWeight="medium" color="text">{r.occasion}</MDTypography>
                          </MDBox>
                        </MDBox>
                        {r.groupe_affecte_nom && (
                          <MDBox display="flex" alignItems="center" gap={1}
                            sx={{ bgcolor: "#e8f5e9", borderRadius: 2, px: 1.5, py: 0.75, mb: 1.5 }}>
                            <Icon sx={{ fontSize: 15, color: "#2e7d32" }}>music_note</Icon>
                            <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#2e7d32" }}>
                              Groupe affecté : {r.groupe_affecte_nom}
                            </MDTypography>
                          </MDBox>
                        )}
                        <MDBox display="flex" gap={1} flexWrap="wrap">
                          <MDButton color="info" size="small" variant="outlined" onClick={() => handleOpenFiche(r)}>
                            <Icon>visibility</Icon>&nbsp;Voir
                          </MDButton>
                          <MDButton color="secondary" size="small" variant="outlined" onClick={() => handleOpenHistorique(r)}>
                            <Icon>history</Icon>
                          </MDButton>
                          {r.statut !== "archivee" && (
                            <MDButton color="info" size="small" variant="outlined" onClick={() => handleOpenModifier(r)}>
                              <Icon>edit</Icon>
                            </MDButton>
                          )}
                          {r.statut === "en_attente" && (
                            <>
                              <MDButton color="success" size="small" onClick={() => handleOpenAffecter(r)}>
                                <Icon>group_add</Icon>&nbsp;Affecter
                              </MDButton>
                              <MDButton color="error" size="small" onClick={() => handleOpenAction(r, "refuser")}>
                                <Icon>close</Icon>&nbsp;Refuser
                              </MDButton>
                            </>
                          )}
                          {r.statut === "confirmee" && (
                            <MDButton color="warning" size="small" onClick={() => handleRevoquer(r.id)}>
                              <Icon>undo</Icon>&nbsp;Révoquer
                            </MDButton>
                            
                          )}
                          {r.statut === "refusee" && (
  <MDButton
    color="info"
    size="small"
    variant="outlined"
    onClick={() => handleReconsiderer(r.id)}
  >
    <Icon>swap_horiz</Icon>&nbsp;Modifier le statut
  </MDButton>
)}
                          <MDButton color="error" size="small" variant="outlined" onClick={() => handleDelete(r.id)}>
                            <Icon>cancel</Icon>
                          </MDButton>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                  {reservationsFiltrees.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune réservation pour ce filtre.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Modifier (Admin) */}
      <Dialog open={openModifier} onClose={() => setOpenModifier(false)} fullWidth maxWidth="md">
        <DialogTitle>Modifier la réservation</DialogTitle>
        <DialogContent>
          <MDBox mb={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </MDBox>
          <MDBox mt={2}>{stepContent[activeStep]}</MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenModifier(false)} color="secondary">Annuler</MDButton>
          {activeStep > 0 && (
            <MDButton onClick={() => setActiveStep((s) => s - 1)} color="info" variant="outlined">Précédent</MDButton>
          )}
          {activeStep < steps.length - 1 ? (
            <MDButton onClick={() => setActiveStep((s) => s + 1)} color="info">Suivant</MDButton>
          ) : (
            <MDButton onClick={handleSubmitModifier} color="info" disabled={loading}>
              {loading ? "Enregistrement..." : "Enregistrer"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Historique */}
      <Dialog open={openHistorique} onClose={handleCloseHistorique} fullWidth maxWidth="md">
        <DialogTitle>
          Historique — {selected?.directeur_nom} ({selected?.directeur_ville})
        </DialogTitle>
        <DialogContent>
          {historique.length === 0 ? (
            <MDBox textAlign="center" py={3}>
              <Icon sx={{ fontSize: 48, color: "text.secondary" }}>history</Icon>
              <MDTypography variant="body2" color="text" mt={1}>Aucune modification enregistrée.</MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                {historique.length} modification(s)
              </MDTypography>
              {historique.map((h) => (
                <Card key={h.id} sx={{ mb: 1.5, border: "1px solid #eee" }}>
                  <MDBox px={2} py={1.5}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ fontSize: 16, color: "info.main" }}>edit</Icon>
                        <MDTypography variant="button" fontWeight="medium">
                          {champsLabels[h.champ_modifie] || h.champ_modifie}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text">
                        {new Date(h.modifie_le).toLocaleDateString("fr-FR")} à{" "}
                        {new Date(h.modifie_le).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <MDBox px={1.5} py={0.5} sx={{ bgcolor: "#ffebee", borderRadius: 1, flex: 1 }}>
                        <MDTypography variant="caption" sx={{ color: "#c62828" }} display="block">Avant</MDTypography>
                        <MDTypography variant="body2" sx={{ color: "#b71c1c" }}>{h.ancienne_valeur || "—"}</MDTypography>
                      </MDBox>
                      <Icon sx={{ color: "text.secondary" }}>arrow_forward</Icon>
                      <MDBox px={1.5} py={0.5} sx={{ bgcolor: "#e8f5e9", borderRadius: 1, flex: 1 }}>
                        <MDTypography variant="caption" sx={{ color: "#2e7d32" }} display="block">Après</MDTypography>
                        <MDTypography variant="body2" sx={{ color: "#1b5e20" }}>{h.nouvelle_valeur || "—"}</MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              ))}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseHistorique} color="info">Fermer</MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Affecter */}
      <Dialog open={openAffecter} onClose={handleCloseAffecter} fullWidth maxWidth="sm">
        <DialogTitle>Affecter un groupe de chants</DialogTitle>
        <DialogContent>
          {selected && (
            <MDBox mb={2}>
              <MDTypography variant="body2">
                Réservation de <strong>{selected.directeur_nom}</strong> — {selected.directeur_ville}
              </MDTypography>
              <MDTypography variant="body2">
                Date : <strong>{new Date(selected.date).toLocaleDateString("fr-FR")}</strong>
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Langue souhaitée : {selected.langue_chants || "Non précisée"}
              </MDTypography>
            </MDBox>
          )}
          <Divider />
          <MDTypography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Groupes disponibles ce jour :</MDTypography>
          <List>
            {groupesDisponibilites.map((g) => (
              <ListItem key={g.id}
                sx={{ border: "1px solid", borderColor: g.disponible ? "success.main" : "error.main",
                  borderRadius: 2, mb: 1, opacity: g.disponible ? 1 : 0.5,
                  cursor: g.disponible ? "pointer" : "not-allowed",
                  bgcolor: groupeSelectionne === g.id ? "#e8f5e9" : "transparent" }}
                onClick={() => g.disponible && setGroupeSelectionne(g.id)}>
                <ListItemIcon>
                  <Radio checked={groupeSelectionne === g.id} disabled={!g.disponible} color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="body2" fontWeight="medium">{g.nom}</MDTypography>
                      <Chip label={g.disponible ? "Disponible" : "Déjà affecté"} size="small"
                        sx={{ bgcolor: g.disponible ? "#43a047" : "#e53935", color: "white" }} />
                    </MDBox>
                  }
                  secondary={
                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block">
                        {Array.isArray(g.type_chants) ? g.type_chants.join(", ") : g.type_chants}
                        {" — "}{g.ville_origine}{" — Matériel : "}{g.materiel_fourni ? "Oui" : "Non"}
                      </MDTypography>
                      {!g.disponible && g.reservations?.map((r, i) => (
                        <MDTypography key={i} variant="caption" sx={{ color: "#e53935" }} display="block">
                          Affecté à : {r.directeur_nom} ({r.directeur_ville}) — {r.occasion} à {r.heure}
                        </MDTypography>
                      ))}
                    </MDBox>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseAffecter} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleAffecter} color="success" disabled={!groupeSelectionne || loading}>
            {loading ? "Affectation..." : "Confirmer l'affectation"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Fiche */}
      <Dialog open={openFiche} onClose={handleCloseFiche} fullWidth maxWidth="md">
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <span>Fiche de réservation</span>
            {selected && (
              <Chip label={statutLabels[selected.statut]} size="small"
                sx={{ bgcolor: statutColors[selected.statut], color: "white" }} />
            )}
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selected && (
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDBox sx={{ bgcolor: "#e3f2fd", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1565c0" }} mb={1}>
                  Informations générales
                </MDTypography>
                <Row label="Édition" value={selected.edition_libelle} />
                <Row label="Directeur" value={selected.directeur_nom} />
                <Row label="Ville" value={selected.directeur_ville} />
                <Row label="Occasion" value={selected.occasion} />
                <Row label="Date" value={new Date(selected.date).toLocaleDateString("fr-FR")} />
                <Row label="Heure" value={selected.heure?.slice(0, 5)} />
                <Row label="Nombre de personnes" value={selected.nb_personnes} />
              </MDBox>
              <MDBox sx={{ bgcolor: "#f3e5f5", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#6a1b9a" }} mb={1}>Lieu</MDTypography>
                <Row label="Type de lieu" value={selected.lieu_type} />
                <Row label="Lieu précis" value={selected.lieu} />
                <Row label="Détails" value={selected.lieu_precision} />
              </MDBox>
              {(selected.occasion_education || selected.occasion_autre) && (
                <MDBox sx={{ bgcolor: "#fff3e0", borderRadius: 2, p: 2 }}>
                  <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#e65100" }} mb={1}>
                    Occasion éducative
                  </MDTypography>
                  <Row label="Type" value={selected.occasion_education} />
                  <Row label="Précision" value={selected.occasion_autre} />
                </MDBox>
              )}
              <MDBox sx={{ bgcolor: "#e8f5e9", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#2e7d32" }} mb={1}>
                  Groupe de chants
                </MDTypography>
                <Row label="Groupe demandé" value={selected.groupe_nom} />
                <Row label="Langue souhaitée" value={selected.langue_chants} />
                <Row label="Groupe local" value={selected.groupe_local ? "Oui" : "Non"} />
                <Row label="Ville du groupe souhaité" value={selected.groupe_ville_origine} />
                <Row label="Matériel audio nécessaire" value={selected.materiel_audio ? "Oui" : "Non"} />
                {selected.groupe_affecte_nom && (
                  <MDTypography variant="body2" sx={{ color: "#1b5e20", mt: 0.5 }}>
                    <strong>Groupe affecté :</strong> {selected.groupe_affecte_nom}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox sx={{ bgcolor: "#fce4ec", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#880e4f" }} mb={1}>
                  Responsables et présentateurs
                </MDTypography>
                <Row label="Responsable fête" value={selected.responsable_fete} />
                <Row label="Responsable adjoint 1" value={selected.responsable_adjoint1} />
                <Row label="Responsable adjoint 2" value={selected.responsable_adjoint2} />
                <Row label="Présentateur 1" value={selected.presentateur1} />
                <Row label="Présentateur 2" value={selected.presentateur2} />
                <Row label="Responsable table principale" value={selected.responsable_table} />
              </MDBox>
              <MDBox sx={{ bgcolor: "#e0f2f1", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#004d40" }} mb={1}>Équipes</MDTypography>
                <Row label="Équipe réception" value={selected.equipe_reception} />
                <Row label="Équipe sécurité" value={selected.equipe_securite} />
                <Row label="Équipe service" value={selected.equipe_service} />
                <Row label="Équipe nettoyage" value={selected.equipe_nettoyage} />
                <Row label="Équipe vente" value={selected.equipe_vente} />
                <Row label="Équipe restauration" value={selected.equipe_restauration} />
              </MDBox>
              <MDBox sx={{ bgcolor: "#fff8e1", borderRadius: 2, p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#f57f17" }} mb={1}>Divers</MDTypography>
                <Row label="Récitateur 1" value={selected.recitateur1} />
                <Row label="Récitateur 2" value={selected.recitateur2} />
                <Row label="Cadeaux" value={selected.cadeaux} />
                <Row label="Publicité radio" value={selected.publicite_radio ? "Oui" : "Non"} />
                <Row label="Notes" value={selected.notes} />
              </MDBox>
              {selected.motif_refus && (
                <MDBox sx={{ bgcolor: "#ffebee", borderRadius: 2, p: 2 }}>
                  <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#c62828" }} mb={1}>
                    Motif de refus
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "#b71c1c" }}>{selected.motif_refus}</MDTypography>
                </MDBox>
              )}
              <MDTypography variant="caption" color="text">
                Demande créée le : {new Date(selected.created_at).toLocaleDateString("fr-FR")}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseFiche} color="info">Fermer</MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Refuser */}
      <Dialog open={openAction} onClose={handleCloseAction} fullWidth maxWidth="sm">
        <DialogTitle>Refuser la réservation</DialogTitle>
        <DialogContent>
          {selected && (
            <MDBox mb={2}>
              <MDTypography variant="body2">Groupe : <strong>{selected.groupe_nom}</strong></MDTypography>
              <MDTypography variant="body2">
                Date : <strong>{new Date(selected.date).toLocaleDateString("fr-FR")}</strong>
              </MDTypography>
              <MDTypography variant="body2">Directeur : <strong>{selected.directeur_nom}</strong></MDTypography>
            </MDBox>
          )}
          <TextField label="Motif du refus" fullWidth multiline rows={3} value={motifRefus}
            onChange={(e) => setMotifRefus(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseAction} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleValider} color="error" disabled={loading || !motifRefus}>
            {loading ? "Traitement..." : "Refuser"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Reservations;